import { useSetProcessorJobCompletionMutation } from '@consigli/hooks';
import { ProcessorJob, ProcessorJobStatus } from '@consigli/types';
import { useMemo, useCallback } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import { ActionButton } from '@/components';
import { errorToast, successToast } from '@/components/toast';

interface PipelineActionButtonProps {
  pipeline: ProcessorJob;
  projectId: number;
  onViewDetails: (pipeline: ProcessorJob) => void;
}

export const PipelineActionButton = ({
  pipeline,
  projectId,
  onViewDetails,
}: PipelineActionButtonProps) => {
  const [setProcessorJobCompletion, { isLoading }] = useSetProcessorJobCompletionMutation();

  const handleViewDetails = useCallback(() => {
    onViewDetails(pipeline);
  }, [onViewDetails, pipeline]);

  const handleStart = useCallback(async () => {
    try {
      await setProcessorJobCompletion({
        projectId,
        jobId: pipeline.id,
        status: ProcessorJobStatus.STARTED,
      }).unwrap();
      successToast({ message: 'Pipeline started successfully' });
    } catch (error) {
      console.error('Failed to start pipeline:', error);
      errorToast({ message: 'Failed to start pipeline' });
    }
  }, [setProcessorJobCompletion, projectId, pipeline.id]);

  const handleStop = useCallback(async () => {
    try {
      await setProcessorJobCompletion({
        projectId,
        jobId: pipeline.id,
        status: ProcessorJobStatus.STOPPED,
      }).unwrap();
      successToast({ message: 'Pipeline stopped successfully' });
    } catch (error) {
      console.error('Failed to stop pipeline:', error);
      errorToast({ message: 'Failed to stop pipeline' });
    }
  }, [setProcessorJobCompletion, projectId, pipeline.id]);

  const actions = useMemo(() => {
    const baseActions = [
      {
        label: 'View Details',
        onClick: handleViewDetails,
        disabled: false,
        destructive: false,
      },
    ];

    // Add start/stop actions based on current status
    if (
      pipeline.status === ProcessorJobStatus.INITIALIZED ||
      pipeline.status === ProcessorJobStatus.STOPPED
    ) {
      baseActions.push({
        label: 'Start',
        onClick: handleStart,
        disabled: isLoading,
        destructive: false,
      });
    }

    if (pipeline.status === ProcessorJobStatus.STARTED) {
      baseActions.push({
        label: 'Stop',
        onClick: handleStop,
        disabled: isLoading,
        destructive: true,
      });
    }

    return baseActions;
  }, [pipeline.status, isLoading, handleStart, handleStop, handleViewDetails]);

  return <ActionButton actions={actions} icon={<BsThreeDots />} />;
};
